import React, { useRef } from 'react';
import styled from 'styled-components';
import { Statistic } from 'components/common';
import { colours } from 'utils/theme';
import { siNumber } from 'utils/number';
import { useSelector } from 'react-redux';
import {
  getOrganisationResearchersTotals,
  getOrganisationTradeShowTotals,
  getOrganisationClinicalTrialTotals,
  getOrganisationView,
  getOrganisationWorkItemTotals,
} from 'selectors/organisation';
import {
  exactFigures,
  hasDenominator,
  useOrgProfileShowsSubset,
} from 'components/Organisation/filterSummary';
import {
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONTRADESHOWSVIEW,
  ORGANISATIONWORKITEMVIEW,
} from 'components/Organisation/helpers';

const Stats = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-around;
  margin-bottom: ${p => p.theme.gutter};
  > div {
    flex: 1;
    margin-right: ${p => p.theme.gutter};
    :last-child {
      margin-right: 0px;
    }
  }
`;

const Total = styled.span`
  font-size: 15px;
  font-weight: normal;
  color: ${p => p.theme.grey};
`;

const ResearcherStatistics = ({
  organisation,
  loading,
  displayWhenZero = 0,
}) => {
  const view = useSelector(getOrganisationView);
  const researcherTotals = useSelector(
    getOrganisationResearchersTotals(organisation.id)
  );
  const workItemTotals = useSelector(
    getOrganisationWorkItemTotals(organisation.id)
  );
  const tradeshowTotals = useSelector(
    getOrganisationTradeShowTotals(organisation.id)
  );
  const clinicalTrialTotals = useSelector(
    getOrganisationClinicalTrialTotals(organisation.id)
  );
  const showsSubset = useOrgProfileShowsSubset(organisation.id);

  let totals;

  if (view === ORGANISATIONPEOPLEVIEW) {
    totals = researcherTotals;
  } else if (view === ORGANISATIONWORKITEMVIEW) {
    totals = workItemTotals;
  } else if (view === ORGANISATIONTRADESHOWSVIEW) {
    totals = tradeshowTotals;
  } else if (view === ORGANISATIONCLINICALTRIALSVIEW) {
    totals = clinicalTrialTotals;
  }

  // Only People and Activity read the shared filter slice; Tradeshows and Clinical
  // Trials keep their own search state, so a job title set over here narrows
  // nothing they count and must not appear to.
  const filtersApplyToView =
    view === ORGANISATIONPEOPLEVIEW || view === ORGANISATIONWORKITEMVIEW;

  // These figures are the last search's, so the denominator has to be decided when
  // they arrive and left alone until the next set does. Reading the live filters
  // would move it as the user edits the panel — before pressing Search — making it
  // look as though the numbers beside it had already changed. A new totals object
  // is the only thing that means a search came back.
  const figuresShown = useRef(undefined);
  const subsetWhenSearched = useRef(false);
  if (figuresShown.current !== totals) {
    figuresShown.current = totals;
    subsetWhenSearched.current = showsSubset;
  }

  const isFiltered = filtersApplyToView && subsetWhenSearched.current;

  /**
   * The pair of props a tile needs to show "18 of 112" and to give the precise
   * figures back on hover.
   *
   * The organisation-wide figure sits beside the filtered one so a narrowed
   * profile cannot be mistaken for the whole organisation; the tooltip covers the
   * case where rounding leaves the two looking identical.
   *
   * @param {number | undefined} value what the current search matched
   * @param {number | undefined} total the whole organisation, from getSuperOrganisation
   */
  const against = (value, total) => {
    const showTotal = hasDenominator(value, total, isFiltered);
    return {
      suffix: showTotal ? <Total>of {siNumber(total)}</Total> : undefined,
      tooltip: exactFigures(value, showTotal ? total : undefined),
    };
  };

  if (loading) return null;
  return (
    <Stats>
      <Statistic
        icon='users'
        iconColor={colours.posters}
        title='People'
        value={totals ? totals.people : displayWhenZero}
        {...against(totals?.people, organisation.totalResearchers)}
      />
      {/* <Statistic
        icon='email'
        iconColor={colours.posters}
        title='Emails'
        value={totals ? totals.emailCount : displayWhenZero}
      /> */}
      <Statistic
        icon='map'
        iconColor={colours.publications}
        title='Countries'
        value={totals ? totals.countries : displayWhenZero}
        {...against(totals?.countries, organisation.totalCountries)}
      />
      <Statistic
        icon='folderOpen'
        iconColor={colours.publications}
        title='Activities'
        value={totals ? totals.activities : displayWhenZero}
        {...against(totals?.activities, organisation.totalActivities)}
      />
    </Stats>
  );
};

export default ResearcherStatistics;
